import React, { useMemo } from "react";
import { WindowSizeProvider } from "./src/shared/context/window-size.context";
import DeviceProvider from "./src/shared/context/device.context";
import CartProvider from "./src/shared/context/cart.context";
import { MenuDataProvider } from "./src/shared/context/menu-data.context";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { plPL } from "@mui/x-date-pickers/locales";
import { enUS } from "@mui/x-date-pickers/locales";
import SiteProvider from "./src/shared/context/site-state.context";
import { NavProvider } from "./src/shared/context";
import { NewLayout } from "./src/components/new-components";
import NotificationProvider from "./src/shared/context/notification.context";
import AuthProvider from "./src/shared/context/auth.context";
import CookieConsentComponent from "./src/components/cookie-consent/cookie-consent";
import { CookieConsentLanguageSync } from "./src/components/cookie-consent/cookie-consent-language-sync";
import { useLanguageDetector } from "./src/shared/locales/languageDetector";
import { useTranslation } from "react-i18next";
import { pl } from "date-fns/locale/pl";
import { enUS as enUSDateFns } from "date-fns/locale/en-US";
import "vanilla-cookieconsent/dist/cookieconsent.css";
import "./styles.scss";

interface WrapperArgs {
  children: React.ReactNode;
}

export const ProvidersWrapper = ({ children }: WrapperArgs) => {
  // Initialize language detection after hydration
  useLanguageDetector();
  const { i18n } = useTranslation();

  // Map i18n language to date-fns locale and MUI locale text
  const { dateFnsLocale, muiLocaleText } = useMemo(() => {
    switch (i18n.language) {
      case "pl":
        return {
          dateFnsLocale: pl,
          muiLocaleText:
            plPL.components.MuiLocalizationProvider.defaultProps.localeText,
        };
      case "en":
        return {
          dateFnsLocale: enUSDateFns,
          muiLocaleText:
            enUS.components.MuiLocalizationProvider.defaultProps.localeText,
        };
      default:
        return {
          dateFnsLocale: pl,
          muiLocaleText:
            plPL.components.MuiLocalizationProvider.defaultProps.localeText,
        };
    }
  }, [i18n.language]);

  return (
    <LocalizationProvider
      dateAdapter={AdapterDateFns}
      adapterLocale={dateFnsLocale}
      localeText={muiLocaleText}
    >
      <MenuDataProvider>
      <CartProvider>
        <WindowSizeProvider>
          <NotificationProvider>
            <AuthProvider>
              <SiteProvider>
                <DeviceProvider>
                  <NavProvider>
                    <CookieConsentLanguageSync />
                    {children}
                  </NavProvider>
                </DeviceProvider>
              </SiteProvider>
            </AuthProvider>
          </NotificationProvider>
        </WindowSizeProvider>
      </CartProvider>
      </MenuDataProvider>
    </LocalizationProvider>
  );
};

export const LayoutWrapper = ({ children }: WrapperArgs) => {
  return <NewLayout>{children}</NewLayout>;
};
